// Radio field - mode-aware. A radio group only ever allows ONE selected value, so it
// renders as a single-select dropdown (tag trigger + chevron + menu) rather than a row of
// pill toggle buttons — a row of pills reads as multi-select even though only one applies
// (OPA-307, same fix already applied to the hero's maturity-stage picker: "stage deberia ser
// dropdown por que solo se puede seleccionar una opcion"). This is a global fix: every
// section across every tab that uses RadioField picks it up automatically.
// Edit/Create: interactive dropdown.
// View/Admin-view: read-only pill showing the selected option (no chevron, not clickable).
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldWrapper } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import type { BaseFieldProps } from '../../types/fields';

export function RadioField({ name, options = [] }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;
  const selectedOption = options.find((opt) => opt.value === value);

  // Read-only: selected value as a single pill, no dropdown affordance
  if (isReadOnly) {
    return (
      <FieldWrapper>
        {selectedOption ? (
          <span
            title={selectedOption.label}
            className="inline-flex max-w-[220px] items-center truncate rounded-full bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary mt-2"
          >
            {selectedOption.label}
          </span>
        ) : (
          <span className="text-sm text-gray-500 mt-2 inline-block">
            {t('common.clickToEnter')}
          </span>
        )}
      </FieldWrapper>
    );
  }

  // Editable: single-select dropdown, same tag-trigger + menu language as
  // FieldStatusDropdown/HeroMaturityStageBadge
  return (
    <FieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="dropdown mt-2">
            <label
              tabIndex={0}
              title={selectedOption ? selectedOption.label : undefined}
              className="inline-flex max-w-[220px] items-center gap-1.5 cursor-pointer rounded-full bg-primary/15 px-3 py-1.5 text-sm font-medium text-primary"
            >
              <span className="truncate">
                {selectedOption ? selectedOption.label : t('common.select')}
              </span>
              <svg
                className="w-3.5 h-3.5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </label>
            <ul
              tabIndex={0}
              className="dropdown-content z-[999] mt-1 py-1 bg-[#2a2a2a] rounded-lg shadow-lg border border-white/10 min-w-40"
            >
              {options.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      field.onChange(opt.value);
                      (document.activeElement as HTMLElement)?.blur();
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                      field.value === opt.value ? 'bg-white/10 text-white' : 'text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      />
      <FieldError message={error} />
    </FieldWrapper>
  );
}
